import { useEffect, useRef, useState } from "react";

import { Download, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";

import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { SearchableSelect } from "~/components/ui/searchable-select";
import { Textarea } from "~/components/ui/textarea";
import { useOilTypes, useRawMaterials } from "~/hooks/purchasing";
import { useProducts } from "~/hooks/useProducts";
import type {
  RecipeFormData,
  RecipeIngredientFormData,
  SkuRecipe,
} from "~/types/production";
import { UNIT_LABELS } from "~/types/purchasing";
import type { RecipeChangeDiffItem } from "~/types/recipeChange";

// "direct" saves straight to the recipe (superadmin/admin). "request" submits a
// change request that a second authorizer must approve before it takes effect.
export type RecipeDialogMode = "direct" | "request";

export interface RecipeSubmitMeta {
  reason: string;
  changes: RecipeChangeDiffItem[];
}

interface RecipeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipe?: SkuRecipe;
  onSubmit: (data: RecipeFormData, meta?: RecipeSubmitMeta) => void;
  isLoading: boolean;
  mode?: RecipeDialogMode;
}

const emptyIngredient: RecipeIngredientFormData = {
  quantity_per_box: 0,
  waste_factor: 1.0, // Default to no waste (1.0 = 0%)
};

export function RecipeDialog({
  open,
  onOpenChange,
  recipe,
  onSubmit,
  isLoading,
  mode = "direct",
}: RecipeDialogProps) {
  const { products } = useProducts();
  const { data: oilTypes = [] } = useOilTypes();
  const { data: rawMaterials = [] } = useRawMaterials();

  const [reason, setReason] = useState("");

  // Filter out materials excluded from inventory (they can't be used in recipes)
  const availableRawMaterials = rawMaterials.filter(
    (m) => !m.exclude_from_inventory
  );

  const [formData, setFormData] = useState<RecipeFormData>({
    product_id: "",
    name: "",
    is_default: false,
    description: "",
    ingredients: [],
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleCsvUpload = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadTemplate = () => {
    const templateData = [
      { CODIGO: "01040010", CANTIDAD: 1 },
      { CODIGO: "01090023", CANTIDAD: 1 },
      { CODIGO: "02040001", CANTIDAD: 0.01 },
    ];
    const worksheet = XLSX.utils.json_to_sheet(templateData);
    // Set column widths
    worksheet["!cols"] = [
      { wch: 15 }, // CODIGO
      { wch: 12 }, // CANTIDAD
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ingredientes");
    XLSX.writeFile(workbook, "plantilla_ingredientes.xlsx");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet);

      const newIngredients: RecipeIngredientFormData[] = [];
      const notFound: string[] = [];

      for (const row of rows) {
        const code = (row["CODIGO"] ?? row["codigo"] ?? row["Codigo"])?.toString();
        const cantidad = parseFloat(
          (row["CANTIDAD"] ?? row["cantidad"] ?? row["Cantidad"] ?? row["CANTID"] ?? "0").toString()
        );

        if (!code) continue;

        const material = availableRawMaterials.find((m) => m.code === code);
        if (material) {
          newIngredients.push({
            raw_material_id: material.id,
            quantity_per_box: cantidad,
            waste_factor: 1,
          });
        } else {
          notFound.push(code);
        }
      }

      if (newIngredients.length > 0) {
        setFormData((prev) => ({
          ...prev,
          ingredients: [...prev.ingredients, ...newIngredients],
        }));
        toast.success(`${newIngredients.length} ingredientes agregados`);
      }

      if (notFound.length > 0) {
        toast.error(`Códigos no encontrados: ${notFound.join(", ")}`);
      }
    } catch (error) {
      toast.error("Error al leer el archivo");
      console.error("CSV parse error:", error);
    }

    // Reset file input so same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    setReason("");
    if (recipe) {
      setFormData({
        product_id: recipe.product_id,
        name: recipe.name,
        is_default: recipe.is_default,
        description: recipe.description || "",
        ingredients:
          recipe.ingredients?.map((ing) => ({
            id: ing.id,
            raw_material_id: ing.raw_material_id || undefined,
            oil_type_id: ing.oil_type_id || undefined,
            quantity_per_box: ing.quantity_per_box,
            waste_factor: ing.waste_factor,
            notes: ing.notes || undefined,
          })) || [],
      });
    } else {
      setFormData({
        product_id: "",
        name: "",
        is_default: false,
        description: "",
        ingredients: [],
      });
    }
  }, [recipe, open]);

  // Resolve an ingredient row to its code + name for the reviewer-facing diff.
  const resolveIngredientLabel = (
    ingredient: Pick<RecipeIngredientFormData, "oil_type_id" | "raw_material_id">
  ): { code: string; name: string } => {
    if (ingredient.oil_type_id) {
      const oil = oilTypes.find((o) => o.id === ingredient.oil_type_id);
      return { code: oil?.code || "", name: oil?.name || "Aceite" };
    }
    if (ingredient.raw_material_id) {
      const material = rawMaterials.find(
        (m) => m.id === ingredient.raw_material_id
      );
      return { code: material?.code || "", name: material?.name || "Material" };
    }
    return { code: "", name: "—" };
  };

  // Build the old → new diff between the original recipe and the edited form.
  const computeChanges = (): RecipeChangeDiffItem[] => {
    const out: RecipeChangeDiffItem[] = [];
    const original = recipe?.ingredients ?? [];
    const origById = new Map(original.map((i) => [i.id, i]));

    if (recipe && formData.name !== recipe.name) {
      out.push({
        code: "",
        name: "Nombre de la receta",
        field: "Nombre",
        old: recipe.name,
        new: formData.name,
      });
    }
    if (recipe && (formData.description || "") !== (recipe.description || "")) {
      out.push({
        code: "",
        name: "Descripción",
        field: "Descripción",
        old: recipe.description || "—",
        new: formData.description || "—",
      });
    }
    if (recipe && formData.is_default !== recipe.is_default) {
      out.push({
        code: "",
        name: "Receta por defecto",
        field: "Por defecto",
        old: recipe.is_default ? "Sí" : "No",
        new: formData.is_default ? "Sí" : "No",
      });
    }

    const seen = new Set<string>();
    for (const ing of formData.ingredients) {
      const label = resolveIngredientLabel(ing);
      if (ing.id && origById.has(ing.id)) {
        seen.add(ing.id);
        const o = origById.get(ing.id)!;
        if (Number(o.quantity_per_box) !== Number(ing.quantity_per_box)) {
          out.push({
            ...label,
            field: "Cantidad/Caja",
            old: o.quantity_per_box,
            new: ing.quantity_per_box,
          });
        }
        if (Number(o.waste_factor) !== Number(ing.waste_factor)) {
          out.push({
            ...label,
            field: "Merma (factor)",
            old: o.waste_factor,
            new: ing.waste_factor,
          });
        }
      } else {
        out.push({
          ...label,
          field: "Ingrediente",
          old: null,
          new: `Agregado (${ing.quantity_per_box})`,
        });
      }
    }
    for (const o of original) {
      if (!seen.has(o.id)) {
        const label = resolveIngredientLabel({
          oil_type_id: o.oil_type_id || undefined,
          raw_material_id: o.raw_material_id || undefined,
        });
        out.push({
          ...label,
          field: "Ingrediente",
          old: `${o.quantity_per_box}`,
          new: "Eliminado",
        });
      }
    }
    return out;
  };

  const handleAddIngredient = () => {
    setFormData((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, { ...emptyIngredient }],
    }));
  };

  const handleRemoveIngredient = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index),
    }));
  };

  const handleIngredientChange = (
    index: number,
    field: keyof RecipeIngredientFormData,
    value: unknown
  ) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: prev.ingredients.map((ing, i) => {
        if (i !== index) return ing;

        // If changing ingredient type, reset the other type
        if (field === "oil_type_id") {
          return { ...ing, [field]: value, raw_material_id: undefined };
        }
        if (field === "raw_material_id") {
          return { ...ing, [field]: value, oil_type_id: undefined };
        }

        return { ...ing, [field]: value };
      }),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "request") {
      if (!reason.trim()) {
        toast.error("Indique el motivo del cambio para la autorización");
        return;
      }
      onSubmit(formData, { reason: reason.trim(), changes: computeChanges() });
      return;
    }
    onSubmit(formData);
  };

  // Get the unit label for an ingredient
  const getIngredientUnit = (ingredient: RecipeIngredientFormData): string => {
    if (ingredient.oil_type_id) {
      return "kg"; // Oil is measured in kilograms per box
    }
    if (ingredient.raw_material_id) {
      const material = rawMaterials.find(
        (m) => m.id === ingredient.raw_material_id
      );
      return material
        ? UNIT_LABELS[material.unit] || material.unit
        : "unidades";
    }
    return "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-7xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{recipe ? "Editar Receta" : "Nueva Receta"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="product_id">Producto *</Label>
            <SearchableSelect
              value={formData.product_id}
              onValueChange={(value) =>
                setFormData((prev) => ({ ...prev, product_id: value }))
              }
              options={products.map((p) => ({
                value: p.id,
                label: p.sku ? `${p.sku} - ${p.short_name}` : p.short_name,
                searchTerms: `${p.sku || ""} ${p.short_name} ${p.barcode || ""}`,
              }))}
              placeholder="Seleccionar producto"
              searchPlaceholder="Buscar producto..."
              emptyMessage="No se encontraron productos"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Nombre de la Receta *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Ej: Receta Estándar Aceite 500ml"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Notas adicionales sobre la receta..."
              rows={2}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Ingredientes</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleDownloadTemplate}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Plantilla
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCsvUpload}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Importar CSV
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddIngredient}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar Ingrediente
                </Button>
              </div>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={handleFileChange}
            />

            {formData.ingredients.length === 0 ? (
              <div className="rounded-lg border border-dashed py-8 text-center text-muted-foreground">
                No hay ingredientes. Agregue ingredientes para esta receta.
              </div>
            ) : (
              <div className="space-y-3">
                {formData.ingredients.map((ingredient, index) => (
                  <Card key={index}>
                    <CardContent className="pt-4">
                      <div className="grid grid-cols-12 items-end gap-3">
                        <div className="col-span-5">
                          <Label className="text-xs">Tipo</Label>
                          <SearchableSelect
                            value={
                              ingredient.oil_type_id
                                ? `oil:${ingredient.oil_type_id}`
                                : ingredient.raw_material_id
                                  ? `material:${ingredient.raw_material_id}`
                                  : ""
                            }
                            onValueChange={(value) => {
                              const [type, id] = value.split(":");
                              if (type === "oil") {
                                handleIngredientChange(
                                  index,
                                  "oil_type_id",
                                  id
                                );
                              } else {
                                handleIngredientChange(
                                  index,
                                  "raw_material_id",
                                  id
                                );
                              }
                            }}
                            groups={[
                              {
                                label: "Aceites",
                                options: oilTypes.map((oil) => ({
                                  value: `oil:${oil.id}`,
                                  label: oil.code
                                    ? `${oil.code} - ${oil.name}`
                                    : oil.name,
                                  searchTerms: `${oil.name} ${oil.code || ""}`,
                                })),
                              },
                              {
                                label: "Materiales",
                                options: availableRawMaterials.map((material) => ({
                                  value: `material:${material.id}`,
                                  label: material.code
                                    ? `${material.code} - ${material.name}`
                                    : material.name,
                                  searchTerms: `${material.name} ${material.code || ""}`,
                                })),
                              },
                            ]}
                            placeholder="Seleccionar"
                            searchPlaceholder="Buscar ingrediente..."
                            emptyMessage="No se encontraron ingredientes"
                          />
                        </div>

                        <div className="col-span-3">
                          <Label className="text-xs">Cantidad/Caja</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              step="any"
                              min="0"
                              value={ingredient.quantity_per_box || ""}
                              onChange={(e) =>
                                handleIngredientChange(
                                  index,
                                  "quantity_per_box",
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              placeholder="0.00"
                            />
                            {(ingredient.oil_type_id ||
                              ingredient.raw_material_id) && (
                              <span className="whitespace-nowrap text-xs text-muted-foreground">
                                {getIngredientUnit(ingredient)}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="col-span-3">
                          <Label className="text-xs">Notas</Label>
                          <Input
                            value={ingredient.notes || ""}
                            onChange={(e) =>
                              handleIngredientChange(
                                index,
                                "notes",
                                e.target.value
                              )
                            }
                            placeholder="Opcional"
                          />
                        </div>

                        <div className="col-span-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveIngredient(index)}
                            className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {mode === "request" && (
            <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <Label htmlFor="change-reason" className="text-sm font-semibold">
                Motivo del cambio *
              </Label>
              <p className="text-xs text-muted-foreground">
                Este cambio no se aplicará hasta que sea autorizado por otra
                persona del área.
              </p>
              <Textarea
                id="change-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ej: Corrección de unidad de medida del film verde (0.33 → 0.00033)"
                rows={2}
              />
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading
                ? "Guardando..."
                : mode === "request"
                  ? "Solicitar cambio"
                  : recipe
                    ? "Guardar Cambios"
                    : "Crear Receta"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
